/**
 * Idle Screen Overlay Component
 * Renders the idle screen with proper layered structure
 * Supports three display modes: default, custom image, custom animation
 */

import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { KioskUICustomization, PendingMediaUpload } from '../../types/kiosk-types';
import { OvalGradient } from './OvalGradient';

interface IdleScreenOverlayProps {
  screenSize: 'small' | 'medium'; // 'small' = 4", 'medium' = 5"
  config: KioskUICustomization;
  pendingIdleScreen?: PendingMediaUpload;
  showInPreview: boolean;
}

type DisplayMode = 'default' | 'custom-image' | 'custom-animation';

/**
 * Idle Screen Overlay Component
 * Renders layered idle screen based on available media
 */
export const IdleScreenOverlay = React.memo(
  ({ screenSize, config, pendingIdleScreen, showInPreview }: IdleScreenOverlayProps) => {
    const [lottieData, setLottieData] = useState<any>(null);
    const [displayMode, setDisplayMode] = useState<DisplayMode>('default');
    const [mediaSrc, setMediaSrc] = useState<string | null>(null);

    // Determine display mode and media source
    useEffect(() => {
      // Priority 1: Pending upload
      if (pendingIdleScreen) {
        if (pendingIdleScreen.mediaType === 'lottie') {
          setDisplayMode('custom-animation');
          setMediaSrc(pendingIdleScreen.previewUrl);
        } else {
          setDisplayMode('custom-image');
          setMediaSrc(pendingIdleScreen.previewUrl);
        }
        return;
      }

      // Priority 2: Saved config media
      const savedMedia =
        screenSize === 'small'
          ? config.icons.media4IdleScreen
          : config.icons.media5IdleScreen;

      if (savedMedia?.s3Url) {
        const isLottie = savedMedia.contentType?.includes('json');
        setDisplayMode(isLottie ? 'custom-animation' : 'custom-image');
        setMediaSrc(savedMedia.s3Url);
        return;
      }

      // Priority 3: Default mode
      setDisplayMode('default');
      setMediaSrc(null);
    }, [pendingIdleScreen, config, screenSize]);

    // Load Lottie animation data
    useEffect(() => {
      if (displayMode === 'default') {
        // Load default Lottie animation
        const animationPath =
          screenSize === 'small'
            ? '/animations/positive_background_animation_small.json'
            : '/animations/positive_background_animation.json';

        fetch(animationPath)
          .then((res) => res.json())
          .then((data) => setLottieData(data))
          .catch((err) => {
            console.error('Failed to load default Lottie animation:', err);
            setLottieData(null);
          });
      } else if (displayMode === 'custom-animation' && mediaSrc) {
        // Load custom Lottie animation
        fetch(mediaSrc)
          .then((res) => res.json())
          .then((data) => setLottieData(data))
          .catch((err) => {
            console.error('Failed to load custom Lottie animation:', err);
            setLottieData(null);
          });
      } else {
        setLottieData(null);
      }
    }, [displayMode, mediaSrc, screenSize]);

    if (!showInPreview) {
      return null;
    }

    const isDefaultMode = displayMode === 'default';
    const isCustomImage = displayMode === 'custom-image';
    const isCustomAnimation = displayMode === 'custom-animation';

    // Calculate header height to preserve top bar visibility
    // 4" screen: Header with logo and title = 103px (as per KioskHeader component)
    // 5" screen: Logo + buttons (~80px) + Title + Divider (~60px) = ~140px
    const headerHeight = screenSize === 'small' ? '103px' : '140px';

    // Footer height to preserve payment animation visibility
    // 4" screen: 60px, 5" screen: 52px
    const footerHeight = screenSize === 'small' ? '60px' : '52px';

    return (
      <div
        className="absolute flex items-center justify-center"
        style={{
          top: headerHeight,
          left: 0,
          right: 0,
          bottom: footerHeight,
          backgroundColor: '#0A0A0A',
          zIndex: 50,
        }}
      >
        {/* Layer 1: Background Lottie Animation (z-10) */}
        {isDefaultMode && lottieData && (
          <div
            className="absolute inset-0"
            style={{ zIndex: 10 }}
          >
            <Lottie
              animationData={lottieData}
              loop={true}
              autoplay={true}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </div>
        )}

        {/* Layer 1: Custom Lottie Animation (z-10) - fitCenter */}
        {isCustomAnimation && lottieData && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ zIndex: 10 }}
          >
            <Lottie
              animationData={lottieData}
              loop={true}
              autoplay={true}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            />
          </div>
        )}

        {/* Layer 1 Alternative: Custom Image (z-10) */}
        {isCustomImage && mediaSrc && (
          <div
            className="absolute inset-0"
            style={{ zIndex: 10 }}
          >
            <img
              src={mediaSrc}
              alt="Idle screen"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                console.error('Failed to load idle screen image:', e);
              }}
            />
          </div>
        )}

        {/* Layer 2: Oval Background (z-20) - Default mode only */}
        {isDefaultMode && <OvalGradient size={screenSize} />}

        {/* Layer 3: Top Fade Gradient (z-30) - All modes */}
        <div
          className="absolute top-0 left-0 right-0"
          style={{
            height: '56px',
            background: 'linear-gradient(180deg, rgba(10, 10, 10, 0.8) 0%, rgba(10, 10, 10, 0) 100%)',
            zIndex: 30,
          }}
          aria-hidden="true"
        />

        {/* Layer 4: Title Text (z-40) - Default mode only */}
        {isDefaultMode && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ zIndex: 40 }}
          >
            <h1
              style={{
                fontFamily: 'Outfit, Roboto, sans-serif',
                fontSize: '44px',
                fontWeight: 400,
                color: '#FFFFFF',
                textAlign: 'center',
                margin: 0,
                padding: '0 20px',
                lineHeight: 1.2,
              }}
            >
              Tap to continue
            </h1>
          </div>
        )}
      </div>
    );
  }
);

IdleScreenOverlay.displayName = 'IdleScreenOverlay';
